import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  DebtDTO,
  DebtChargeDTO,
  DebtPaymentDTO,
  DebtStatus,
  DebtType,
  InterestRateType,
} from '@shared/types';

export function useDebts(status?: DebtStatus) {
  return useQuery({
    queryKey: ['debts', status],
    queryFn: async () => (await api.get<DebtDTO[]>('/debts', { params: { status } })).data,
  });
}

export function useDebt(debtId: number | undefined) {
  return useQuery({
    queryKey: ['debts', debtId],
    queryFn: async () => (await api.get<DebtDTO>(`/debts/${debtId}`)).data,
    enabled: debtId != null,
  });
}

export interface CreateDebtInput {
  name: string;
  creditor?: string | null;
  type: DebtType;
  principal: number;
  interestRate: number;
  interestRateType: InterestRateType;
  termMonths?: number | null;
  installmentAmount?: number | null;
  creditLimit?: number | null;
  minimumPayment?: number | null;
  startDate: string;
  dueDay?: number | null;
  notes?: string | null;
}

export function useCreateDebt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateDebtInput) => (await api.post<DebtDTO>('/debts', input)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['debts'] }),
  });
}

export function useUpdateDebt(debtId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<CreateDebtInput>) => (await api.patch<DebtDTO>(`/debts/${debtId}`, input)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['debts', debtId] });
      queryClient.invalidateQueries({ queryKey: ['debts'] });
    },
  });
}

export function useDebtPayments(debtId: number | undefined) {
  return useQuery({
    queryKey: ['debts', debtId, 'payments'],
    queryFn: async () => (await api.get<DebtPaymentDTO[]>(`/debts/${debtId}/payments`)).data,
    enabled: debtId != null,
  });
}

export function useCreatePayment(debtId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { amount: number; paidAt: string; note?: string | null }) =>
      (await api.post<DebtPaymentDTO>(`/debts/${debtId}/payments`, input)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['debts', debtId] });
      queryClient.invalidateQueries({ queryKey: ['debts'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useDebtCharges(debtId: number | undefined) {
  return useQuery({
    queryKey: ['debts', debtId, 'charges'],
    queryFn: async () => (await api.get<DebtChargeDTO[]>(`/debts/${debtId}/charges`)).data,
    enabled: debtId != null,
  });
}
